
import { defineComponent } from "vue";
import { getTmoa, updateTmoaUser } from "@/service/tmoa.service";
import { Filter } from "@/types/filter.type";
import { Tmoa, TmoaDistributionDays, TmoaDistributionHours } from "@/types/tmoa.type";
import { getSamplingPointById } from "@/service/samplingpoint.service";
import { runScriptTmoa } from "@/service/tmoa.service";
import { SamplingPoint } from "@/types/samplingpoint.type";
import { dateFormatDate, formatDate } from "@/utils/date.utils";

interface DynamicObject {
  [key: string]: string;
}
export default defineComponent({
  // components: { RangeDateComponent },
  setup() {
    return { dateFormatDate, formatDate };
  },
  data() {
    return {
      samplingPoint: {} as SamplingPoint,
      infoTmoaCopy: [] as Tmoa[],
      distributionDays: [] as TmoaDistributionDays[],
      distributionHours: [] as TmoaDistributionHours[],
      transitions: [] as any[],
      show: false,
      rangeDate: "",
      selectedTmoa: {} as Tmoa, // Guarda la TMOA seleccionada para editar
      editedTmoa: {} as Tmoa,
      openEditModal: false,
    };
  },

  async mounted() {
    await this.getInfoTmoa();
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },

    close() {
      this.show = false;
    },

    async sendParams(value: { start: Date; end: Date }) {
      this.close();
      await runScriptTmoa(value.start, value.end, this.samplingPoint);
      await this.getInfoTmoa();
    },

    async getInfoTmoa() {
      const infoTmoa = await this.getTmoaBySamplingPoint();
      this.rangeDate = `(${formatDate(this.samplingPoint?.startRangeTmoa)} - ${formatDate(
        this.samplingPoint?.endRangeTmoa
      )}) `;
      this.infoTmoaCopy = this.sortByType([...infoTmoa]);
      this.samplingPoint = await getSamplingPointById(
        parseInt(this.$route.query.samplingPoint as string)
      );

      if (infoTmoa) {
        this.createDistributionDays(this.infoTmoaCopy);
        this.createDistributionHours(this.infoTmoaCopy);
        const props = this.getUniqueProperties(this.infoTmoaCopy);
        const objetoConProps = this.initializeObject(props.sort());
        this.transitions = [...new Set(this.createTransitions(this.infoTmoaCopy, objetoConProps))];
      }
    },

    sortByType(arrayTmoa: Tmoa[]) {
      return arrayTmoa.sort((a: Tmoa, b: Tmoa) => {
        // Extraer el número del nombre "TMOA X"
        if (a.type !== undefined && b.type !== undefined) {
          const numeroA = parseInt(a.type.replace("TMOA ", ""));
          const numeroB = parseInt(b.type.replace("TMOA ", ""));

          // Comparar los números y devolver el resultado
          return numeroA - numeroB;
        } else {
          return 0;
        }
      });
    },

    async getTmoaBySamplingPoint() {
      const samplingPointId = parseInt(this.$route.query.samplingPoint as string);
      const filter: Filter = {
        fields: {
          "tmoa.samplingPointId": { operator: "equal", value: samplingPointId },
        },
      };
      return getTmoa(filter);
    },

    createDistributionDays(infoTmoa: Tmoa[]) {
      this.distributionDays = infoTmoa.flatMap((element) => {
        if (element.tmoaDistributionDays && element.tmoaDistributionDays.length > 0) {
          return element.tmoaDistributionDays.map((day) => {
            day.type = element.type;
            return day as TmoaDistributionDays;
          });
        }
        return []; // Devuelve un array vacío si no hay tmoaDistributionDays
      });
    },

    createDistributionHours(infoTmoa: Tmoa[]) {
      this.distributionHours = infoTmoa.flatMap((element) => {
        if (element.tmoaDistributionHours && element.tmoaDistributionHours.length > 0) {
          return element.tmoaDistributionHours.map((hour) => {
            hour.type = element.type;
            return hour as TmoaDistributionHours;
          });
        }
        return []; // Devuelve un array vacío si no hay tmoaDistributionDays
      });
    },

    // Obtener todas las propiedades únicas "tmoa_y"
    getUniqueProperties(infoTmoa: Tmoa[]): string[] {
      const tmoaTransitions = infoTmoa.flatMap((element) => element.tmoaTransitions);
      return [
        ...new Set(
          tmoaTransitions
            .map((objeto) => objeto?.tmoa_y)
            .filter((prop): prop is string => typeof prop === "string")
        ),
      ];
    },

    // Inicializar objeto con propiedades y valores predeterminados
    initializeObject(props: string[]) {
      const objetoConProps: DynamicObject = { type: "" };
      props.forEach((prop) => {
        objetoConProps[prop] = "-"; // Asigna null o cualquier valor predeterminado a cada propiedad
      });
      return objetoConProps;
    },

    createTransitions(infoTmoa: Tmoa[], objetoConProps: DynamicObject) {
      return infoTmoa.flatMap((element) => {
        if (element.tmoaTransitions && element.tmoaTransitions.length > 0) {
          const x = "";
          let objTransition = { ...objetoConProps };
          return element.tmoaTransitions.map((hour) => {
            if (hour.tmoa_x !== x) {
              if (hour.tmoa_x && hour.tmoa_y) {
                objTransition.type = hour.tmoa_x;
                objTransition[hour.tmoa_y] =
                  hour.percentage !== undefined && hour.percentage !== null
                    ? `${hour.percentage}`
                    : "-";
                return objTransition;
              }
            }
          });
        }
        return []; // Devuelve un array vacío si no hay tmoaDistributionDays
      });
    },

    editTmoa(row: any) {
      this.selectedTmoa = row;
      this.editedTmoa = { ...row };
      this.openEditModal = true;
    },

    closeModal() {
      this.editedTmoa = { ...this.selectedTmoa };
      this.openEditModal = false;
    },

    async saveTmoa() {
      this.selectedTmoa = { ...this.editedTmoa };
      await updateTmoaUser(this.editedTmoa.id, this.editedTmoa.name, this.editedTmoa.description);
      const index = this.infoTmoaCopy.findIndex((item) => item.id === this.editedTmoa.id);
      if (index !== -1) {
        this.infoTmoaCopy.splice(index, 1, { ...this.editedTmoa });
      }
      this.openEditModal = false;
    },
  },
});
